// ComboField — typeable dropdown for identity / flat-list fields
// (Code / Supplier / Catalog / experiment-config). Ported from
// private/nos-housekeeping-features/project/components-standalone/type-selector.jsx
// (ComboField at line 486-656). The trigger IS an <input>: clicking focuses
// it, typing filters the options, and "Use 'X' as custom" commits a new value.

import * as React from "react";
import { createPortal } from "react-dom";
import type { ComboOption } from "./_types";
import { useClickOutside, usePopoverPosition } from "./usePopover";

type Props = {
  value: string;
  onChange: (next: string) => void;
  options: ComboOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  popoverMinWidth?: number | null;
  popoverAlign?: "start" | "end";
  /** Hard cap on the popover's height in px. Defaults to 360. */
  popoverMaxHeight?: number;
  mono?: boolean;
  prefix?: React.ReactNode;
  renderRow?: (o: ComboOption) => React.ReactNode;
  renderHint?: (o: ComboOption) => React.ReactNode;
  allowCustom?: boolean;
  ariaLabel?: string;
};

const CaretDown = ({ size = 11 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Check = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Plus = ({ size = 10 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export function ComboField({
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search, or type a new value…",
  emptyText = "No matches.",
  popoverMinWidth = null,
  popoverAlign = "start",
  popoverMaxHeight = 360,
  mono = true,
  prefix,
  renderRow,
  renderHint,
  allowCustom = true,
  ariaLabel,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [focused, setFocused] = React.useState(false);
  // True once the user types into the input (or clears it). Until then,
  // the trigger displays `value` and the dropdown is unfiltered even though
  // q may already hold the value for replace-on-keystroke behavior.
  const [userEdited, setUserEdited] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const popRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  useClickOutside(
    ref,
    () => {
      setOpen(false);
      setQ("");
      setUserEdited(false);
    },
    popRef,
  );
  const popPos = usePopoverPosition(triggerRef, open, {
    minWidth: popoverMinWidth,
    align: popoverAlign,
  });

  // Always include the current `value` in the option list so the active-row
  // highlight actually has something to apply to. The panel's option builders
  // typically exclude the current row (e.g. Code filters out the current LI
  // by id) — without this, opening the dropdown shows no selected row.
  const optionsWithCurrent = React.useMemo(() => {
    if (!value) return options;
    if (options.some((o) => o.value === value)) return options;
    return [{ value, label: value } as ComboOption, ...options];
  }, [options, value]);

  // Only filter the dropdown once the user starts editing. Before that,
  // show all options even if `q` already mirrors `value` (which it does so
  // the displayed input matches the closed trigger).
  const effectiveQ = userEdited ? q.trim() : "";
  const qLow = effectiveQ.toLowerCase();
  const results = effectiveQ
    ? optionsWithCurrent.filter((o) =>
        `${o.label ?? ""} ${o.value} ${o.hint ?? ""}`.toLowerCase().includes(qLow),
      )
    : optionsWithCurrent;
  const exact = optionsWithCurrent.find(
    (o) => (o.label ?? o.value).toLowerCase() === qLow,
  );
  const canCreate = allowCustom && effectiveQ.length > 0 && !exact;

  const commit = (v: string) => {
    onChange(v);
    setOpen(false);
    setQ("");
    setUserEdited(false);
    inputRef.current?.blur();
  };

  const inputValue = open ? q : value || "";
  const inputPlaceholder = open ? searchPlaceholder : placeholder;

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div
        ref={triggerRef}
        onClick={() => inputRef.current?.focus()}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 9px",
          borderRadius: 5,
          border: `1px solid ${open || focused ? "var(--ink)" : "var(--line-strong)"}`,
          background: "var(--panel)",
          boxShadow: open || focused ? "0 0 0 2px var(--accent-soft)" : "none",
          transition: "border-color 120ms, box-shadow 120ms",
          cursor: "text",
          minHeight: 30,
        }}
      >
        {prefix}
        <input
          ref={inputRef}
          aria-label={ariaLabel}
          value={inputValue}
          onChange={(e) => {
            setQ(e.target.value);
            setUserEdited(true);
            if (!open) setOpen(true);
          }}
          onFocus={(e) => {
            setFocused(true);
            setOpen(true);
            // Mirror the current value into the input AND select all so the
            // first keystroke replaces it. Filtering stays off until that
            // first keystroke (see userEdited above).
            if (value) {
              setQ(value);
              setUserEdited(false);
              const el = e.target;
              window.setTimeout(() => el.select(), 0);
            }
          }}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (exact) commit(exact.value);
              else if (canCreate) commit(effectiveQ);
              else if (results.length === 1 && results[0]) commit(results[0].value);
            }
            if (e.key === "Escape") {
              setOpen(false);
              setQ("");
              setUserEdited(false);
              inputRef.current?.blur();
            }
          }}
          placeholder={inputPlaceholder}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 13,
            fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
            color: "var(--ink)",
            fontWeight: 500,
            padding: "1px 2px",
          }}
        />
        <span
          style={{
            color: "var(--ink-3)",
            display: "inline-flex",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 160ms ease",
            flexShrink: 0,
          }}
        >
          <CaretDown />
        </span>
      </div>

      {open && popPos &&
        createPortal(
          <div
            ref={popRef}
            data-popover-portal="1"
            style={{
              position: "fixed",
              left: popPos.left,
              top: popPos.top,
              bottom: popPos.bottom,
              width: popPos.width,
              zIndex: 1000,
              background: "var(--panel)",
              border: "1px solid var(--line-strong)",
              borderRadius: 8,
              boxShadow: "0 12px 32px -8px rgba(20,30,22,0.22)",
              maxHeight: Math.min(
                popoverMaxHeight,
                Math.max(220, popPos.maxHeight || popoverMaxHeight),
              ),
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ flex: 1, overflow: "auto" }}>
              {results.length === 0 && !canCreate && (
                <div
                  style={{
                    padding: "12px 14px",
                    fontSize: 11.5,
                    color: "var(--ink-4)",
                    fontStyle: "italic",
                    textAlign: "center",
                  }}
                >
                  {emptyText}
                </div>
              )}
              {results.map((o) => {
                const sel = o.value === value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => commit(o.value)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "7px 12px",
                      border: "none",
                      textAlign: "left",
                      background: sel ? "var(--accent-soft)" : "transparent",
                      borderLeft: `3px solid ${sel ? "var(--accent)" : "transparent"}`,
                      cursor: "pointer",
                      color: "inherit",
                    }}
                    onMouseEnter={(e) => {
                      if (!sel) e.currentTarget.style.background = "var(--bg-sunk)";
                    }}
                    onMouseLeave={(e) => {
                      if (!sel) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {renderRow ? (
                        renderRow(o)
                      ) : (
                        <span
                          style={{
                            fontSize: 12,
                            color: "var(--ink-2)",
                            fontFamily: mono
                              ? "var(--font-mono)"
                              : "var(--font-sans)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {o.label ?? o.value}
                        </span>
                      )}
                    </span>
                    {(o.hint || renderHint) && (
                      <span
                        className="mono"
                        style={{
                          fontSize: 9.5,
                          color: "var(--ink-4)",
                          flexShrink: 0,
                        }}
                      >
                        {renderHint ? renderHint(o) : o.hint}
                      </span>
                    )}
                    {sel && (
                      <span style={{ color: "var(--accent-ink)" }}>
                        <Check />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {canCreate && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(effectiveQ)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  background: "var(--accent-soft)",
                  color: "var(--accent-ink)",
                  border: "none",
                  borderTop: "1px solid var(--accent-line)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  textAlign: "left",
                }}
              >
                <Plus size={11} />
                <span style={{ fontSize: 12 }}>
                  Use "
                  <strong
                    style={{
                      fontFamily: mono ? "var(--font-mono)" : "inherit",
                    }}
                  >
                    {effectiveQ}
                  </strong>
                  " as custom
                </span>
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
